import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide,
  TextField,
  IconButton,
  Grid,
  Typography,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Popover,
} from "@mui/material";
import { FileUploadOutlined, DescriptionOutlined } from "@mui/icons-material";
import axios from "axios";
import { getCookie } from "../../../../function";
import getArticleService from "../../../../services/getArticleService";
import getPaymentModeService from "../../../../services/getPaymentModeService";
import { NotificationManager } from "react-notifications";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const UploadPaymentProof = ({ setIsOpen, isOpen, articleData, fetchData }) => {
  const [paymentProofs, setPaymentProofs] = useState([]); // Store multiple files
  const [imagePreviews, setImagePreviews] = useState([]); // Previews for multiple images
  const [invoiceFile, setInvoiceFile] = useState(null); // Store invoice PDF
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [paymentModeList, setPaymentModeList] = useState([]);
  const [bankList, setBankList] = useState([]);
  const [formData, setFormData] = useState({
    amount: "",
    remark: "",
    paymentDate: "",
    paymentModeId: "",
    bankId: "",
  });
  const [showCustomPaymentMode, setShowCustomPaymentMode] = useState(false);
  const [customPaymentModeInput, setCustomPaymentModeInput] = useState("");
  const [paymentModeAnchorEl, setPaymentModeAnchorEl] = useState(null);
  const [isPaymentModeLoading, setIsPaymentModeLoading] = useState(false);

  // Ref for Payment Mode Select
  const paymentModeSelectRef = useRef(null);

  useEffect(() => {
    const fetchPaymentModes = async () => {
      try {
        const result = await getPaymentModeService.GetAllPaymentModes();
        console.log("Payment Modes Raw Result:", result);

        if (
          result &&
          (Array.isArray(result) ||
            (result.additionalData && Array.isArray(result.additionalData)))
        ) {
          const rawList = Array.isArray(result)
            ? result
            : result.additionalData;
          const standardizedList = rawList.map((mode) => ({
            id: mode.id || mode.paymentModeId, // Ensure consistent 'id' property
            paymentModeName: mode.paymentModeName,
            // copy other properties if necessary
          }));
          setPaymentModeList(standardizedList);
        } else {
          console.error("Invalid payment modes data structure:", result);
          setPaymentModeList([]);
        }
      } catch (error) {
        console.error("Error fetching payment modes:", error);
        setPaymentModeList([]);
      }
    };

    const fetchBanks = async () => {
      try {
        const result = await getPaymentModeService.GetAllBanks();
        console.log("Banks Raw Result:", result);

        if (
          result &&
          (Array.isArray(result) ||
            (result.additionalData && Array.isArray(result.additionalData)))
        ) {
          const rawList = Array.isArray(result)
            ? result
            : result.additionalData;
          const standardizedList = rawList.map((bank) => ({
            id: bank.id || bank.bankId, // Ensure consistent 'id' property
            bankName: bank.bankName,
            // copy other properties if necessary
          }));
          setBankList(standardizedList);
        } else {
          console.error("Invalid banks data structure:", result);
          setBankList([]);
        }
      } catch (error) {
        console.error("Error fetching banks:", error);
        setBankList([]);
      }
    };

    fetchPaymentModes();
    fetchBanks();
  }, []);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      // Clean up preview URLs when component unmounts
      imagePreviews.forEach(preview => {
        if (preview.type === "image" && preview.url) {
          URL.revokeObjectURL(preview.url);
        }
      });
    };
  }, [imagePreviews]);

  // Calculate remaining fees
  const calculateRemaningFees = () => {
    const totalFees = articleData?.totalfees || 0;
    const totalPaid =
      articleData?.payments?.reduce(
        (sum, payment) => sum + Number(payment.amount),
        0
      ) || 0;
    return totalFees - totalPaid;
  };

  const handleClose = () => {
    setIsOpen(false);
    // Clean up preview URLs to prevent memory leaks
    imagePreviews.forEach(preview => {
      if (preview.type === "image" && preview.url) {
        URL.revokeObjectURL(preview.url);
      }
    });
    setPaymentProofs([]);
    setImagePreviews([]);
    setInvoiceFile(null);
    setFormData({
      amount: "",
      remark: "",
      paymentDate: "",
      paymentModeId: "",
      bankId: "",
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "amount") {
      if (value < 0) {
        NotificationManager.error("Amount cannot be negative", "Error", 3000);
        return;
      }
    } else if (name === "paymentDate") {
      const today = new Date().toISOString().split("T")[0];
      if (value > today) {
        NotificationManager.error(
          "Payment date cannot be in the future",
          "Error",
          3000
        );
        return;
      }
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleInvoiceUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type === "application/pdf") {
        setInvoiceFile(file);
      } else {
        NotificationManager.error(
          "Please upload a PDF file for invoice",
          "Error",
          3000
        );
      }
    }
  };

  const handlePaymentProofUpload = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter((file) =>
      ["image/jpeg", "image/png", "image/gif", "application/pdf"].includes(file.type)
    );

    if (validFiles.length !== files.length) {
      NotificationManager.error(
        "Some files were not JPEG/PNG/GIF images or PDF files and were skipped.",
        "Warning",
        3000
      );
    }

    const previews = validFiles.map((file) => {
      if (file.type === "application/pdf") {
        return { type: "pdf", name: file.name, url: null };
      } else {
        return { type: "image", url: URL.createObjectURL(file) };
      }
    });
    
    setPaymentProofs((prev) => [...prev, ...validFiles]);
    setImagePreviews((prev) => [...prev, ...previews]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (paymentProofs.length === 0) {
      NotificationManager.error(
        "Please upload at least one payment proof.",
        "Error",
        3000
      );
      return;
    }

    if (
      !formData.amount ||
      !formData.paymentDate ||
      !formData.paymentModeId ||
      !formData.bankId
    ) {
      NotificationManager.error(
        "Amount, payment date, payment mode, and bank name are required.",
        "Error",
        3000
      );
      return;
    }

    const amount = Number(formData.amount);
    if (amount <= 0) {
      NotificationManager.error("Amount must be greater than 0", "Error", 3000);
      return;
    }

    setShowConfirmation(true);
  };

  const handleConfirmSubmit = async () => {
    setLoading(true);
    setShowConfirmation(false);

    try {
      let invoiceKey = null;
      const fileKeys = [];

      if (invoiceFile) {
        const presignResponse = await axios.post(
          "https://hquswoext6.execute-api.ap-south-1.amazonaws.com/prod/articles/presignedurl",
          {
            fileName: invoiceFile.name,
            fileType: invoiceFile.type,
            folderName: "articles",
          },
          {
            headers: {
              Authorization: getCookie("token"),
              "x-api-key": "JjgeTensKk20J7GIxBfMSaA25Aricvmr1HYmjxpS",
            },
          }
        );

        const data =
          typeof presignResponse.data === "string"
            ? JSON.parse(presignResponse.data)
            : presignResponse.data;

        const { presignedUrl, fileKey } = data;

        await axios.put(presignedUrl, invoiceFile, {
          headers: { "Content-Type": invoiceFile.type },
        });

        invoiceKey = fileKey;
      }

      for (const file of paymentProofs) {
        const presignResponse = await axios.post(
          "https://hquswoext6.execute-api.ap-south-1.amazonaws.com/prod/articles/presignedurl",
          {
            fileName: file.name,
            fileType: file.type,
            folderName: "paymentProofs",
          },
          {
            headers: {
              Authorization: getCookie("token"),
              "x-api-key": "JjgeTensKk20J7GIxBfMSaA25Aricvmr1HYmjxpS",
            },
          }
        );

        const data =
          typeof presignResponse.data === "string"
            ? JSON.parse(presignResponse.data)
            : presignResponse.data;

        const { presignedUrl, fileKey } = data;

        await axios.put(presignedUrl, file, {
          headers: { "Content-Type": file.type },
        });

        fileKeys.push(fileKey);
      }

      const paymentData = {
        amount: formData.amount,
        invoiceKey: invoiceKey,
        paymentDate: formData.paymentDate,
        paymentProofFileKeys: fileKeys,
        remark: formData.remark || "",
        PublicationId: articleData.PublicationId,
        paymentModeId: formData.paymentModeId,
        bankId: formData.bankId,
        paymentMode:
          paymentModeList.find((mode) => mode.id === formData.paymentModeId)
            ?.paymentModeName || "",
        bank:
          bankList.find((bank) => bank.id === formData.bankId)?.bankName || "",
      };

      const response = await getArticleService.UploadPaymentProof(
        paymentData,
        articleData.id
      );

      if (response.success) {
        NotificationManager.success(response.message, "Success", 2000);
        fetchData();
        handleClose();
      } else {
        NotificationManager.error(response.message, "Error", 5000);
      }
    } catch (error) {
      console.error("Error uploading files:", error);
      NotificationManager.error("Upload failed", "Error", 5000);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentModeChange = (e) => {
    const value = e.target.value;
    if (value === "other") {
      setPaymentModeAnchorEl(paymentModeSelectRef.current);
      setShowCustomPaymentMode(true);
    } else {
      setFormData((prev) => ({
        ...prev,
        paymentModeId: value,
      }));
    }
  };

  const handleBankChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      bankId: value,
    }));
  };

  const handleCustomPaymentModeInput = (e) => {
    setCustomPaymentModeInput(e.target.value);
  };

  const handleAddCustomPaymentMode = async () => {
    if (!customPaymentModeInput.trim()) {
      NotificationManager.error("Please enter a payment mode name");
      return;
    }

    setIsPaymentModeLoading(true);
    try {
      const result = await getPaymentModeService.AddPaymentMode({
        paymentModeName: customPaymentModeInput.trim(),
      });
      console.log("AddPaymentMode Service Result:", result);

      if (result && result.success && result.additionalData) {
        NotificationManager.success("Payment mode added successfully");

        const newlyAddedModeData = {
          id: result.additionalData.paymentModeId,
          paymentModeName: result.additionalData.paymentModeName,
        };

        setPaymentModeList((prev) => [...prev, newlyAddedModeData]);
        setFormData((prev) => ({
          ...prev,
          paymentModeId: newlyAddedModeData.id,
        }));
        handleClosePaymentModePopover();
      } else {
        NotificationManager.error(
          result?.data?.error || "Failed to add payment mode"
        );
      }
    } catch (error) {
      console.error("Error adding payment mode:", error);
      NotificationManager.error("Failed to add payment mode");
    } finally {
      setIsPaymentModeLoading(false);
    }
  };

  const handleClosePaymentModePopover = () => {
    setPaymentModeAnchorEl(null);
    setShowCustomPaymentMode(false);
    setCustomPaymentModeInput("");
  };

  return (
    <>
      <Dialog
        open={isOpen}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Upload Payment Proofs</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label=" Payment Amount"
                  name="amount"
                  type="number"
                  value={formData.amount}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Payment Date"
                  name="paymentDate"
                  type="date"
                  value={formData.paymentDate}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  size="small"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    max: new Date().toISOString().split("T")[0],
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required size="small">
                  <InputLabel id="payment-mode-label">Payment Mode</InputLabel>
                  <Select
                    labelId="payment-mode-label"
                    id="paymentMode"
                    name="paymentMode"
                    value={
                      paymentModeList.some(
                        (mode) => mode.id === formData.paymentModeId
                      )
                        ? formData.paymentModeId
                        : ""
                    }
                    onChange={handlePaymentModeChange}
                    label="Payment Mode"
                    ref={paymentModeSelectRef}
                    key={paymentModeList.length}
                  >
                    {paymentModeList.map((mode) => {
                      console.log("MenuItem for Payment Mode:", {
                        key: mode.id,
                        value: mode.id,
                        name: mode.paymentModeName,
                      });
                      return (
                        <MenuItem key={mode.id} value={mode.id}>
                          {mode.paymentModeName}
                        </MenuItem>
                      );
                    })}
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required size="small">
                  <InputLabel id="bank-name-label">Bank Name</InputLabel>
                  <Select
                    labelId="bank-name-label"
                    id="bankName"
                    name="bankName"
                    value={
                      bankList.some((bank) => bank.id === formData.bankId)
                        ? formData.bankId
                        : ""
                    }
                    onChange={handleBankChange}
                    label="Bank Name"
                    key={bankList.length}
                  >
                    {bankList.map((bank) => (
                      <MenuItem key={bank.id} value={bank.id}>
                        {bank.bankName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Remark"
                  name="remark"
                  value={formData.remark}
                  onChange={handleInputChange}
                  variant="outlined"
                  size="small"
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Upload Invoice (PDF)
              </Typography>
              <TextField
                variant="outlined"
                type="text"
                value={invoiceFile ? invoiceFile.name : ""}
                disabled
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: (
                    <IconButton component="label">
                      <DescriptionOutlined />
                      <input
                        type="file"
                        hidden
                        accept="application/pdf"
                        onChange={handleInvoiceUpload}
                      />
                    </IconButton>
                  ),
                }}
              />
            </Box>

            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Upload Payment Proofs (Images & PDFs)
              </Typography>
              {imagePreviews.length > 0 && (
                <div style={{ marginTop: 10, marginBottom: 20 }}>
                  <div
                    style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}
                  >
                    {imagePreviews.map((preview, index) => (
                      <div key={index} style={{ position: "relative" }}>
                        {preview.type === "image" && (
                          <img
                            src={preview.url}
                            alt={`Preview ${index + 1}`}
                            style={{
                              width: "150px",
                              height: "150px",
                              objectFit: "cover",
                              borderRadius: "4px",
                            }}
                          />
                        )}
                        {preview.type === "pdf" && (
                          <div
                            style={{
                              width: "150px",
                              height: "150px",
                              border: "2px dashed #ccc",
                              borderRadius: "4px",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            <DescriptionOutlined style={{ fontSize: 40, color: "#666" }} />
                            <Typography variant="caption" style={{ marginTop: 8, textAlign: "center", padding: "0 8px" }}>
                              {preview.name}
                            </Typography>
                          </div>
                        )}
                        <IconButton
                          size="small"
                          style={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            backgroundColor: "white",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                          }}
                          onClick={() => {
                            // Clean up URL if it's an image
                            if (preview.type === "image" && preview.url) {
                              URL.revokeObjectURL(preview.url);
                            }
                            setPaymentProofs((prev) =>
                              prev.filter((_, i) => i !== index)
                            );
                            setImagePreviews((prev) =>
                              prev.filter((_, i) => i !== index)
                            );
                          }}
                        >
                          ×
                        </IconButton>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <TextField
                variant="outlined"
                type="text"
                value={paymentProofs.map((file) => file.name).join(", ") || ""}
                disabled
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: (
                    <IconButton component="label">
                      <FileUploadOutlined />
                      <input
                        type="file"
                        hidden
                        accept="image/jpeg,image/png,image/gif,application/pdf"
                        multiple
                        onChange={handlePaymentProofUpload}
                      />
                    </IconButton>
                  ),
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions className="pb-3">
            <Button
              variant="contained"
              color="error"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              type="submit"
              disabled={loading}
            >
              {loading ? "Saving..." : "Add Payment"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Payment</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to save this payment? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowConfirmation(false)}
            color="error"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSubmit}
            color="success"
            variant="contained"
          >
            Confirm & Save
          </Button>
        </DialogActions>
      </Dialog>

      <Popover
        open={showCustomPaymentMode}
        anchorEl={paymentModeAnchorEl}
        onClose={handleClosePaymentModePopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box sx={{ p: 2, width: 300 }}>
          <TextField
            fullWidth
            size="small"
            value={customPaymentModeInput}
            onChange={handleCustomPaymentModeInput}
            placeholder="Enter New Payment Mode"
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              size="small"
              onClick={handleClosePaymentModePopover}
              disabled={isPaymentModeLoading}
              variant="text"
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleAddCustomPaymentMode}
              disabled={isPaymentModeLoading || !customPaymentModeInput.trim()}
            >
              {isPaymentModeLoading ? "Adding..." : "Add Payment Mode"}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default UploadPaymentProof;
